import React from 'react'
import { COLORS } from './theme'

// The left nav. One list of everything this person can reach — dashboards and
// apps together — because "is that a dashboard or an app?" is an implementation
// detail of which repo serves it, not a question the reader should have to
// answer before they can find what they came for.
//
// It is NOT rendered on a wallboard. See App.jsx: a TV showing a nav is showing
// a menu nobody is standing close enough to click.

const SECTION_LABEL = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: COLORS.textMuted,
    padding: '0 16px',
    marginBottom: 6,
}

// Values come from GET /api/apps/health: 'ok' | 'error' | 'coming_soon'.
const HEALTH = {
    ok: { color: COLORS.success, title: 'Responding' },
    error: { color: COLORS.danger, title: 'Not responding' },
    coming_soon: { color: COLORS.border, title: 'Not deployed yet' },
}

function HealthDot({ status }) {
    // Undefined means "not checked yet", which is deliberately NOT rendered as
    // down. A confident wrong signal is worse than no signal — the same rule the
    // hub applies to `unavailable` versus a real zero.
    const { color, title } = HEALTH[status] || { color: COLORS.border, title: 'Health unknown' }
    return (
        <span
            title={title}
            style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: color,
                flexShrink: 0,
                boxShadow: status === 'ok' ? `0 0 6px ${COLORS.success}66` : 'none',
            }}
        />
    )
}

function NavItem({ label, sublabel, active, onClick, trailing }) {
    return (
        <button
            onClick={onClick}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                textAlign: 'left',
                padding: '8px 16px',
                border: 'none',
                borderLeft: `2px solid ${active ? COLORS.primary : 'transparent'}`,
                background: active ? 'rgba(59,130,246,0.10)' : 'transparent',
                color: active ? COLORS.text : COLORS.textMuted,
                font: 'inherit',
                fontSize: 14,
                cursor: 'pointer',
                transition: 'background 120ms, color 120ms',
            }}
            onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'
            }}
            onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = 'transparent'
            }}
        >
            {trailing}
            <span style={{ overflow: 'hidden' }}>
                <span
                    style={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: active ? 600 : 400,
                    }}
                >
                    {label}
                </span>
                {sublabel && (
                    <span
                        style={{
                            display: 'block',
                            fontSize: 11,
                            color: COLORS.textMuted,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {sublabel}
                    </span>
                )}
            </span>
        </button>
    )
}

export default function Nav({
    user,
    apps,
    health,
    views,
    viewsError,
    route,
    onNavigate,
    onLogout,
    isAdmin,
    hasReports = false,
}) {
    const isDash = (key) => route.view === 'dashboards' && route.viewKey === key
    const isApp = (id) => route.view === 'app' && route.appId === id

    return (
        <nav
            style={{
                width: 248,
                flexShrink: 0,
                borderRight: `1px solid ${COLORS.border}`,
                background: 'rgba(15,23,42,0.6)',
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                position: 'sticky',
                top: 0,
            }}
        >
            <div style={{ padding: '18px 16px 16px' }}>
                <button
                    onClick={() => onNavigate({ view: 'home' })}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        textAlign: 'left',
                        font: 'inherit',
                    }}
                >
                    <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>
                        B-Stock Internal Tools
                    </div>
                </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 12 }}>
                <div style={{ marginBottom: 20 }}>
                    <div style={SECTION_LABEL}>Dashboards</div>
                    {viewsError ? (
                        <div style={{ padding: '4px 16px', fontSize: 12, color: COLORS.warning }}>
                            {viewsError}
                        </div>
                    ) : views.length === 0 ? (
                        <div style={{ padding: '4px 16px', fontSize: 12, color: COLORS.textMuted }}>
                            None for your role
                        </div>
                    ) : (
                        views.map((v) => (
                            <NavItem
                                key={v.key}
                                label={v.name}
                                sublabel={v.audience}
                                active={isDash(v.key)}
                                onClick={() => onNavigate({ view: 'dashboards', viewKey: v.key })}
                            />
                        ))
                    )}
                </div>

                {hasReports && (
                    <div style={{ marginBottom: 20 }}>
                        <div style={SECTION_LABEL}>People</div>
                        <NavItem
                            label="My team"
                            sublabel="12 weeks, no rank"
                            active={route.view === 'team'}
                            onClick={() => onNavigate({ view: 'team' })}
                        />
                    </div>
                )}

                <div style={{ marginBottom: 20 }}>
                    <div style={SECTION_LABEL}>Apps</div>
                    {apps.length === 0 ? (
                        <div style={{ padding: '4px 16px', fontSize: 12, color: COLORS.textMuted }}>
                            None for your role
                        </div>
                    ) : (
                        apps.map((a) => (
                            <NavItem
                                key={a.id}
                                label={a.name}
                                active={isApp(a.id)}
                                trailing={<HealthDot status={health[a.id]} />}
                                onClick={() => onNavigate({ view: 'app', appId: a.id })}
                            />
                        ))
                    )}
                </div>

                {isAdmin && (
                    <div>
                        <div style={SECTION_LABEL}>Manage</div>
                        <NavItem
                            label="Admin"
                            active={route.view === 'admin'}
                            onClick={() => onNavigate({ view: 'admin' })}
                        />
                    </div>
                )}
            </div>

            <div
                style={{
                    borderTop: `1px solid ${COLORS.border}`,
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                }}
            >
                <span
                    style={{
                        fontSize: 12,
                        color: COLORS.textMuted,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                    title={user.email}
                >
                    {user.name || user.email}
                </span>
                <button
                    onClick={onLogout}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: COLORS.textMuted,
                        fontSize: 12,
                        cursor: 'pointer',
                        padding: 0,
                        flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.color = COLORS.danger
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.color = COLORS.textMuted
                    }}
                >
                    Sign out
                </button>
            </div>
        </nav>
    )
}
